import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Divider, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, WidgetCard, ChartLineBar, RingGauge, BarChartCustom, IRingGaugeProps, ILabelsAndValuesRingGaugeProps } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import { CompanyDTO, CompanyFilterDTO } from '../../../shared/interfaces';
import { ChartBar } from '../../../shared/components/e-charts/bar/ChartBar';
import Calendar from '../../../shared/components/Calendar/calendar';
import { useTranslation } from 'react-i18next';

export const TalentSphereDashboard: React.FC = () => {


    const [companyList, setDashboardList] = useState<CompanyDTO[]>([]);
    const [companyFilterDTO, setDashboardFilterDTO] = useState<CompanyFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);
    const { t } = useTranslation();

    // calendar
    const [selectedDate, setSelectedDate] = useState(new Date());

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
    };
    //calendar
    
    useEffect(() => {
        if (actionExecuted)
            handleDashboardGetAllViewAsync();
    }, [actionExecuted]);

    const handleDashboardGetAllViewAsync = useCallback(async () => {
        if (companyFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('corpsphere.company', companyFilterDTO, setActionExecuted, setLoading, setDashboardList, showMessage, true);
    }, [companyFilterDTO]);

    const handleClearFilterClick = () => {
        return;
    };

    function handleSearchClick() {
        handleDashboardGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        // { id: 'new', text: 'New Dashboard', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('talentSphereDashboard.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'associateUsers', toolTip: t('talentSphereDashboard.cardActions.associateUsers'), icon: 'group_outlined', iconColor: 'info', setAction: handleAssociateUsers },
        { id: 'delete', toolTip: t('talentSphereDashboard.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('talentSphereDashboard.chipFilters.totalRecords'), value: companyList.length },
        { label: t('talentSphereDashboard.chipFilters.name'), value: companyFilterDTO?.name },
        { label: t('talentSphereDashboard.chipFilters.code'), value: companyFilterDTO?.code },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('talentSphereDashboard.cardColumns.name'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'code', label: t('talentSphereDashboard.cardColumns.code'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'mainActivity', label: t('talentSphereDashboard.cardColumns.mainActivity'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'businessRelationshipType', label: t('talentSphereDashboard.cardColumns.businessRelationshipType'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: t('talentSphereDashboard.cardColumns.status'), size: 4, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'unityType', label: t('talentSphereDashboard.cardColumns.unityType'), size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
    ];

    const labelAndValuesRingoGauge: ILabelsAndValuesRingGaugeProps = { 
        centerLabel: t('talentSphereDashboard.labelAndValuesRingoGauge.totalExpected'), centerValue: 1,
        label1: '', value1: 1, 
        label2: '', value2: 2, 
        label3: '', value3: 3, 
        label4: '', value4: 4, 
        label5: '', value5: 5, 
        leftLabel: '', leftValue: 1
    };

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title={t('talentSphereDashboard.layoutDashboard.title')}>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label={t('talentSphereDashboard.textFieldInput.name')}
                                setValue={setDashboardFilterDTO}
                                value={companyFilterDTO?.name}
                                obj={companyFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='code'
                                label={t('talentSphereDashboard.textFieldInput.code')}
                                setValue={setDashboardFilterDTO}
                                value={companyFilterDTO?.code}
                                obj={companyFilterDTO}
                                placeholder={'Code'}
                                maxLength={200}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <Grid container spacing={1}>
                        {/* <Grid item xs={12} md={12}>
                            <Calendar
                                currentDate={selectedDate}
                                onDateClick={handleDateClick}
                            />
                        </Grid> */}
                        <Grid item xs={12} md={2}>
                            <WidgetCard
                                icon={'person_outline'}
                                IconColor={'primary'}
                                fontSizeIcon={'20px'}
                                label={t('talentSphereDashboard.widgetCard.newRequest')}
                                value={10}
                                description={t('talentSphereDashboard.widgetCard.totalNewVacancyRequest')} 
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <WidgetCard
                                icon={'manage_accounts_outline'}
                                IconColor={'info'}
                                fontSizeIcon={'0rem'}
                                label={t('talentSphereDashboard.widgetCard.inProgress')}
                                value={8}
                                description={t('talentSphereDashboard.widgetCard.totalInQualification')}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <WidgetCard
                                icon={'person_add_outline'}
                                IconColor={'success'}
                                fontSizeIcon={'0rem'}
                                label={t('talentSphereDashboard.widgetCard.hired')}
                                value={2}
                                description={t('talentSphereDashboard.widgetCard.totalVacanciesClosed')}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <WidgetCard
                                icon={'person_remove_outline'}
                                IconColor={'error'}
                                fontSizeIcon={'0rem'}
                                label={t('talentSphereDashboard.widgetCard.canceled')}
                                value={20}
                                description={t('talentSphereDashboard.widgetCard.totalVacanciesCanceled')}
                            />
                        </Grid>
                        <Grid item xs={2} md={2}>
                            <WidgetCard
                                icon={'person_off_outline'}
                                IconColor={'warning'}
                                fontSizeIcon={'0rem'}
                                label={t('talentSphereDashboard.widgetCard.suspended')}
                                value={4}
                                description={t('talentSphereDashboard.widgetCard.totalVacanciesSuspended')}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <WidgetCard
                                icon={'people_outline'}
                                IconColor={'secondary'}
                                fontSizeIcon={'0rem'}
                                label={t('talentSphereDashboard.widgetCard.total')}
                                value={3}
                                description={t('talentSphereDashboard.widgetCard.totalVacancies')}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <RingGauge
                                title={t('talentSphereDashboard.ringGauge.slaOnTotal')}
                                subTitle={t('talentSphereDashboard.ringGauge.totalOfTestScenarios')}
                                labelAndValues={labelAndValuesRingoGauge}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <RingGauge
                                title={t('talentSphereDashboard.ringGauge.slaOnOpen')}
                                subTitle={t('talentSphereDashboard.ringGauge.totalOfTestScenarios')}
                                labelAndValues={labelAndValuesRingoGauge}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <RingGauge
                                title={t('talentSphereDashboard.ringGauge.slaOnClosed')}
                                subTitle={t('talentSphereDashboard.ringGauge.totalOfTestScenarios')}
                                labelAndValues={labelAndValuesRingoGauge}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <ChartLineBar
                                title={t('talentSphereDashboard.chartLineBar.salesAnalysis')}
                                subTitle={t('talentSphereDashboard.chartLineBar.salesPerMonth')}
                                chartType='bar'
                                legend={[t('talentSphereDashboard.chartLineBar.legend.pending'), t('talentSphereDashboard.chartLineBar.legend.approved'), t('talentSphereDashboard.chartLineBar.legend.failed'), t('talentSphereDashboard.chartLineBar.legend.blocked')]}
                                dates={[t('talentSphereDashboard.chartLineBar.dates.january'), t('talentSphereDashboard.chartLineBar.dates.february'), t('talentSphereDashboard.chartLineBar.dates.march'), t('talentSphereDashboard.chartLineBar.dates.april'), t('talentSphereDashboard.chartLineBar.dates.may'), t('talentSphereDashboard.chartLineBar.dates.june')]} // abreviar
                                dataSeries={[
                                    [120, 130, 140, 100, 90, 40], // Dados para 'Pendente'
                                    [80, 90, 100, 70, 80, 45],    // Dados para 'Aprovado'
                                    [60, 70, 90, 80, 90, 60],     // Dados para 'Reprovado'
                                    [30, 40, 50, 60, 70, 80]      // Dados para 'Bloqueado'
                                ]}
                                color={['#03a9f4', '#4caf50', '#f44336', '#ffeb3b']}
                                trendSeriesIndex={3}
                                trendSeriesName={t('talentSphereDashboard.chartLineBar.trendSeriesName.blockedTrend')}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <ChartLineBar
                                title={t('talentSphereDashboard.chartLineBar.testExecution')}
                                subTitle={t('talentSphereDashboard.chartLineBar.last15Days')}
                                chartType='line'
                                legend={[t('talentSphereDashboard.chartLineBar.legend.pending'), t('talentSphereDashboard.chartLineBar.legend.approved'), t('talentSphereDashboard.chartLineBar.legend.failed'), t('talentSphereDashboard.chartLineBar.legend.blocked')]}
                                dates={['01/01/2024', '02/01/2014', '03/01/2024', '04/01/2024', '05/01/2024', '06/01/2024', '07/01/2024', '08/01/2024', '09/01/2024', '10/01/2024', '12/01/2024', '13/01/2024', '14/01/2024', '15/01/2024']}
                                dataSeries={[
                                    [120, 130, 140, 100, 90, 40, 20, 80, 10, 20, 40, 20, 40, 60], // Dados para 'Pendente'
                                    [80, 90, 100, 70, 80, 45],    // Dados para 'Aprovado'
                                    [60, 70, 90, 80, 90, 60],     // Dados para 'Reprovado'
                                    [30, 40, 50, 60, 70, 80]      // Dados para 'Bloqueado'
                                ]}
                                color={['#03a9f4', '#4caf50', '#f44336', '#ffeb3b']}
                                trendSeriesIndex={3}
                                trendSeriesName={t('talentSphereDashboard.chartLineBar.trendSeriesName.blockedTrend')}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <ChartBar 
                                
                            />
                        </Grid>
                    </Grid>

                </Box>

            </LayoutDashboard>
        </React.Fragment >
    );
};